import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import type { RootState } from "../../../store/store";

import {
  cancelProcessApi,
  completeProcessApi,
  completeStepApi,
  DpProcessDetail,
  DpProcessStep,
  getProcessApi,
  NOTICE_TYPE_LABELS,
  PROCESS_KIND_LABELS,
  PROCESS_STATUS_BADGE,
  PROCESS_STATUS_LABELS,
  reopenStepApi,
  runStepActionApi,
  skipStepApi,
  STEP_RESPONSIBLE_LABELS,
  STEP_STATUS_LABELS,
  SYSTEM_ACTION_LABELS,
  TERMINATION_TYPE_LABELS,
  updateStepApi,
} from "../api/processesApi";
import {
  documentTypeName,
  DpDocumentType,
  DpEmployeeDocument,
  listDocumentTypesApi,
  listEmployeeDocumentsApi,
} from "../api/documentsApi";
import { getEmployeeApi, DpEmployeeDetail } from "../api/employeesApi";
import { CONTRACT_TYPE_LABELS } from "../api/dpTypes";
import DocumentFileButton from "../components/DocumentFileButton";
import DocumentFormModal from "../components/DocumentFormModal";
import ProvisionalPasswordModal from "../components/ProvisionalPasswordModal";
import TerminationPaymentPanel from "../components/TerminationPaymentPanel";
import { formatDateOnly, orDash, toDateInputValue } from "../utils/format";
import { DP_EMPLOYEE_PAGE, DP_PAGE, DP_PROCESSES_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

const STEP_STATUS_BADGE: Record<DpProcessStep["status"], string> = {
  PENDENTE: "dpBadge--orange",
  CONCLUIDO: "dpBadge--green",
  NAO_APLICAVEL: "dpBadge--gray",
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("pt-BR");
};

type StepModal = { step: DpProcessStep; mode: "complete" | "skip" };

export default function ProcessDetailPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PROCESSES_PAGE());
  const { id } = useParams<{ id: string }>();
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [process, setProcess] = useState<DpProcessDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [stepModal, setStepModal] = useState<StepModal | null>(null);
  const [note, setNote] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [candidates, setCandidates] = useState<DpEmployeeDocument[]>([]);

  const [documentTypes, setDocumentTypes] = useState<DpDocumentType[]>([]);
  const [employee, setEmployee] = useState<DpEmployeeDetail | null>(null);
  const [newDocumentFor, setNewDocumentFor] = useState<DpProcessStep | null>(null);

  const [provisionalPassword, setProvisionalPassword] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const load = useCallback(async () => {
    if (!token || !id) return;
    try {
      setProcess(await getProcessApi(token, id));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o processo.");
      navigate(DP_PROCESSES_PAGE(), { replace: true });
    } finally {
      setLoading(false);
    }
  }, [token, id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    listDocumentTypesApi(token)
      .then(setDocumentTypes)
      .catch(() => setDocumentTypes([]));
  }, [token]);

  /** Documentos vigentes do tipo exigido pela etapa — os que podem cumpri-la. */
  const loadCandidates = useCallback(
    async (step: DpProcessStep) => {
      if (!token || !process || !step.documentTypeId) return setCandidates([]);
      const docs = await listEmployeeDocumentsApi(token, process.employeeId);
      setCandidates(docs.filter((d) => d.documentTypeId === step.documentTypeId));
    },
    [token, process]
  );

  const openStepModal = async (step: DpProcessStep, mode: StepModal["mode"]) => {
    setNote("");
    setDocumentId("");
    setStepModal({ step, mode });
    if (mode === "complete" && step.documentTypeId) {
      try {
        await loadCandidates(step);
      } catch (error: any) {
        toast.error(error?.message || "Erro ao carregar os documentos.");
      }
    }
  };

  const run = async (action: () => Promise<DpProcessDetail>, success: string) => {
    setBusy(true);
    try {
      setProcess(await action());
      toast.success(success);
      return true;
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível concluir a operação.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitStepModal = async () => {
    if (!token || !process || !stepModal) return;
    const { step, mode } = stepModal;

    if (mode === "skip" && note.trim().length < 3) return toast.error("Explique por que a etapa não se aplica.");
    if (mode === "complete" && step.documentTypeId && !documentId) {
      return toast.error("Escolha o documento que cumpre a etapa.");
    }

    const ok = await run(
      () =>
        mode === "skip"
          ? skipStepApi(token, process.id, step.id, note.trim())
          : completeStepApi(token, process.id, step.id, {
              note: note.trim() || null,
              documentId: step.documentTypeId ? documentId : null,
            }),
      mode === "skip" ? "Etapa marcada como não se aplica." : "Etapa concluída."
    );
    if (ok) setStepModal(null);
  };

  const runAction = async (step: DpProcessStep) => {
    if (!token || !process || !step.systemAction) return;
    const question =
      step.systemAction === "CRIAR_CREDENCIAL"
        ? "Criar o acesso do colaborador ao app agora? A senha provisória aparece uma única vez."
        : "Desativar o acesso do colaborador ao app agora?";
    if (!window.confirm(question)) return;

    setBusy(true);
    try {
      const result = await runStepActionApi(token, process.id, step.id);
      const { provisionalPassword: password, ...detail } = result;
      setProcess(detail);
      if (password) setProvisionalPassword(password);
      toast.success("Ação executada.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao executar a ação.");
    } finally {
      setBusy(false);
    }
  };

  const changeDueDate = (step: DpProcessStep, value: string) => {
    if (!token || !process) return;
    run(() => updateStepApi(token, process.id, step.id, { dueDate: value || null }), "Prazo atualizado.");
  };

  const openNewDocument = async (step: DpProcessStep) => {
    if (!token || !process) return;
    try {
      // O formulário precisa dos contratos do colaborador para o vínculo opcional.
      setEmployee(employee ?? (await getEmployeeApi(token, process.employeeId)));
      setNewDocumentFor(step);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o colaborador.");
    }
  };

  const completeProcess = async () => {
    if (!token || !process) return;
    const done = await run(async () => {
      // O desligamento devolve `warnings` quando o demonstrativo de rescisão não foi calculado.
      const { warnings, ...detail } = await completeProcessApi(token, process.id);
      for (const w of warnings ?? []) toast.warning(w, { autoClose: false });
      return detail;
    }, "Processo concluído.");
    if (done) setCompleteOpen(false);
  };

  const cancelProcess = async () => {
    if (!token || !process) return;
    if (cancelReason.trim().length < 3) return toast.error("Informe o motivo do cancelamento.");
    setBusy(true);
    try {
      const result = await cancelProcessApi(token, process.id, cancelReason.trim());
      const { credentialDisabledByProcess, financeiroWarnings, ...detail } = result;
      setProcess(detail);
      setCancelOpen(false);
      toast.success("Processo cancelado.");
      for (const w of financeiroWarnings ?? []) toast.warning(w, { autoClose: false });
      if (credentialDisabledByProcess) {
        toast.warning(
          'O acesso ao app foi desativado por este desligamento. Se o colaborador continua, reative na aba "Acesso ao app".',
          { autoClose: false }
        );
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao cancelar o processo.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) return null;

  const open = process?.status === "EM_ANDAMENTO";
  const pending = process?.steps.filter((s) => s.status === "PENDENTE").length ?? 0;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          {loading || !process ? (
            <div className="dpEmpty">Carregando...</div>
          ) : (
            <>
              <EcoPageHeader
                crumbs={[
                  { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
                  { label: "Admissão e desligamento", onClick: () => navigate(DP_PROCESSES_PAGE()) },
                  { label: process.employee.fullName },
                ]}
                title={`${PROCESS_KIND_LABELS[process.kind]} de ${process.employee.fullName}`}
                subtitle={`${process.template?.name ?? "Sem modelo"} · ${PROCESS_STATUS_LABELS[process.status]}${
                  open ? ` · ${pending} ${pending === 1 ? "etapa pendente" : "etapas pendentes"}` : ""
                }`}
                onBack={goBack}
                backLabel="Voltar"
                actions={
                  <>
                    <span className={`dpBadge ${PROCESS_STATUS_BADGE[process.status]} dpeHeaderBadge`}>
                      {PROCESS_STATUS_LABELS[process.status]}
                    </span>
                    <button
                      type="button"
                      className="ecoBtn"
                      onClick={() => navigate(`${DP_EMPLOYEE_PAGE(process.employeeId)}?aba=processos`)}
                    >
                      <EcoIcon name="users" size={18} />
                      Ficha do colaborador
                    </button>
                  </>
                }
              />

              <section className="dpSection">
                <div className="dpDefinitionGrid">
                  <Field label="Contrato" value={CONTRACT_TYPE_LABELS[process.contract.contractType]} />
                  <Field label="Admissão" value={formatDateOnly(process.contract.admissionDate)} />
                  <Field label="Empregador" value={process.contract.employer.tradeName || process.contract.employer.legalName} />
                  <Field
                    label={process.kind === "ADMISSAO" ? "Data-base (admissão)" : "Último dia trabalhado"}
                    value={formatDateOnly(process.baseDate)}
                  />
                  {process.kind === "DESLIGAMENTO" ? (
                    <>
                      <Field
                        label="Tipo de desligamento"
                        value={process.terminationType ? TERMINATION_TYPE_LABELS[process.terminationType] : "—"}
                      />
                      <Field label="Aviso prévio" value={process.noticeType ? NOTICE_TYPE_LABELS[process.noticeType] : "—"} />
                      <Field label="Início do aviso" value={formatDateOnly(process.noticeStartDate)} />
                      <Field label="Data de término" value={formatDateOnly(process.terminationDate)} />
                      <Field label="Motivo" value={orDash(process.terminationReason)} />
                    </>
                  ) : null}
                  <Field label="Aberto por" value={`${process.createdBy.nomeCompleto} em ${formatDateTime(process.createdAt)}`} />
                  {process.completedAt ? <Field label="Concluído em" value={formatDateTime(process.completedAt)} /> : null}
                  {process.canceledAt ? (
                    <Field label="Cancelado" value={`${formatDateTime(process.canceledAt)} — ${orDash(process.cancelReason)}`} />
                  ) : null}
                </div>
              </section>

              <section className="dpSection">
                <h2 className="dpSection__title">Checklist</h2>
                <div className="dpProcessSteps">
                  {process.steps.map((step) => (
                    <article
                      key={step.id}
                      className={`dpProcessStep ${step.isOverdue ? "is-overdue" : ""} ${step.status !== "PENDENTE" ? "is-done" : ""}`}
                    >
                      <div className="dpProcessStep__head">
                        <span className="dpProcessStep__order">{step.order}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="dpProcessStep__title">{step.title}</div>
                          {step.description ? <div className="dpFormHint">{step.description}</div> : null}
                        </div>
                        <span className={`dpBadge ${STEP_STATUS_BADGE[step.status]}`}>{STEP_STATUS_LABELS[step.status]}</span>
                      </div>

                      <div className="dpProcessStep__meta">
                        <span>Responsável: {STEP_RESPONSIBLE_LABELS[step.responsible]}</span>
                        {open && step.status === "PENDENTE" ? (
                          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            Prazo:
                            <input
                              type="date"
                              className="dpInput"
                              style={{ height: 30, width: "auto" }}
                              value={toDateInputValue(step.dueDate)}
                              disabled={busy}
                              onChange={(e) => changeDueDate(step, e.target.value)}
                            />
                          </label>
                        ) : (
                          <span>Prazo: {formatDateOnly(step.dueDate)}</span>
                        )}
                        {step.isOverdue ? <span className="dpBadge dpBadge--red">Atrasada</span> : null}
                        {step.documentType ? <span>Exige: {step.documentType.name}</span> : null}
                        {step.systemAction ? <span>Ação: {SYSTEM_ACTION_LABELS[step.systemAction]}</span> : null}
                      </div>

                      {step.status !== "PENDENTE" ? (
                        <div className="dpProcessStep__meta">
                          <span>
                            {step.completedBy?.nomeCompleto ?? "—"} em {formatDateTime(step.completedAt)}
                          </span>
                          {step.note ? <span>Obs.: {step.note}</span> : null}
                          {step.document && !step.document.deletedAt ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              Documento de {formatDateOnly(step.document.issuedAt)}
                              {step.document.assetId ? (
                                <DocumentFileButton
                                  token={token}
                                  documentId={step.document.id}
                                  fileName={step.document.fileName}
                                />
                              ) : null}
                            </span>
                          ) : null}
                        </div>
                      ) : null}

                      {open ? (
                        <div className="dpTable__actions" style={{ marginTop: 10 }}>
                          {step.status === "PENDENTE" ? (
                            <>
                              {step.systemAction ? (
                                <button className="dpBtn dpBtn--primary" disabled={busy} onClick={() => runAction(step)}>
                                  {SYSTEM_ACTION_LABELS[step.systemAction]}
                                </button>
                              ) : (
                                <button
                                  className="dpBtn dpBtn--primary"
                                  disabled={busy}
                                  onClick={() => openStepModal(step, "complete")}
                                >
                                  Concluir
                                </button>
                              )}
                              <button
                                className="dpBtn dpBtn--secondary"
                                disabled={busy}
                                onClick={() => openStepModal(step, "skip")}
                              >
                                Não se aplica
                              </button>
                            </>
                          ) : (
                            <button
                              className="dpBtn dpBtn--secondary"
                              disabled={busy}
                              onClick={() =>
                                run(() => reopenStepApi(token, process.id, step.id), "Etapa reaberta.")
                              }
                            >
                              Reabrir
                            </button>
                          )}
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              </section>

              {process.kind === "DESLIGAMENTO" && process.status !== "CANCELADO" ? (
                <section className="dpSection">
                  <h2 className="dpSection__title">Verbas rescisórias (conferência)</h2>
                  {/* Concluir o desligamento troca a origem dos dados (processo → rescisão): recarrega. */}
                  <TerminationPaymentPanel
                    key={process.status}
                    token={token}
                    contractId={process.contract.id}
                    employeeName={process.employee.fullName}
                  />
                </section>
              ) : null}

              {open ? (
                <div className="dpHeader__actions" style={{ justifyContent: "flex-end", marginBottom: 24 }}>
                  <button
                    className="dpBtn dpBtn--secondary"
                    disabled={busy}
                    onClick={() => {
                      setCancelReason("");
                      setCancelOpen(true);
                    }}
                  >
                    Cancelar processo
                  </button>
                  <button
                    className={`dpBtn ${process.kind === "DESLIGAMENTO" ? "dpBtn--danger" : "dpBtn--primary"}`}
                    disabled={busy || pending > 0}
                    title={pending > 0 ? `${pending} etapa(s) pendente(s)` : undefined}
                    onClick={() => setCompleteOpen(true)}
                  >
                    Concluir processo{pending > 0 ? ` (${pending} pendente${pending > 1 ? "s" : ""})` : ""}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </main>

      {/* ── Modal: concluir / não se aplica ─────────────────────────────────── */}
      {stepModal && process ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {stepModal.mode === "skip" ? "Etapa não se aplica" : "Concluir etapa"}
              </h2>
              <button className="dpModal__close" onClick={() => setStepModal(null)} disabled={busy}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <p className="dpNotice">{stepModal.step.title}</p>

              {stepModal.mode === "complete" && stepModal.step.documentTypeId ? (
                <div className="dpFormGroup">
                  <label htmlFor="stepDocument">Documento que cumpre a etapa *</label>
                  {candidates.length === 0 ? (
                    <p className="dpFormHint">
                      O colaborador ainda não tem {stepModal.step.documentType?.name ?? "o documento"} vigente.
                    </p>
                  ) : (
                    <select
                      id="stepDocument"
                      className="dpSelect"
                      value={documentId}
                      disabled={busy}
                      onChange={(e) => setDocumentId(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {candidates.map((d) => (
                        <option key={d.id} value={d.id}>
                          {documentTypeName(d)}
                          {d.title ? ` — ${d.title}` : ""} · emitido em {formatDateOnly(d.issuedAt)}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    type="button"
                    className="dpBtn dpBtn--secondary"
                    style={{ marginTop: 8, alignSelf: "flex-start" }}
                    disabled={busy}
                    onClick={() => openNewDocument(stepModal.step)}
                  >
                    Enviar novo documento
                  </button>
                </div>
              ) : null}

              <div className="dpFormGroup">
                <label htmlFor="stepNote">{stepModal.mode === "skip" ? "Justificativa *" : "Observação"}</label>
                <textarea
                  id="stepNote"
                  className="dpTextarea"
                  maxLength={1000}
                  value={note}
                  disabled={busy}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>
            <footer className="dpModal__footer">
              <button className="dpBtn dpBtn--secondary" onClick={() => setStepModal(null)} disabled={busy}>
                Cancelar
              </button>
              <button className="dpBtn dpBtn--primary" onClick={submitStepModal} disabled={busy}>
                Confirmar
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {/* ── Modal: novo documento a partir da etapa ─────────────────────────── */}
      {newDocumentFor && process && employee ? (
        <DocumentFormModal
          token={token}
          employeeId={process.employeeId}
          documentTypes={documentTypes}
          contracts={employee.contracts}
          fixedTypeId={newDocumentFor.documentTypeId}
          onClose={() => setNewDocumentFor(null)}
          onSaved={async (document) => {
            setNewDocumentFor(null);
            await loadCandidates(newDocumentFor);
            setDocumentId(document.id);
          }}
        />
      ) : null}

      {/* ── Modal: cancelar ─────────────────────────────────────────────────── */}
      <EcoConfirmDialog
        open={completeOpen && !!process}
        title={process?.kind === "DESLIGAMENTO" ? "Concluir o desligamento?" : "Concluir a admissão?"}
        message={
          process?.kind === "DESLIGAMENTO"
            ? "A rescisão será registrada, o acesso do colaborador ao app será desativado e o demonstrativo de rescisão será recalculado."
            : "O processo de admissão será marcado como concluído."
        }
        confirmLabel="Concluir"
        tone={process?.kind === "DESLIGAMENTO" ? "danger" : "default"}
        busy={busy}
        onConfirm={completeProcess}
        onCancel={() => setCompleteOpen(false)}
      />

      {cancelOpen && process ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Cancelar processo</h2>
              <button className="dpModal__close" onClick={() => setCancelOpen(false)} disabled={busy}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <p className="dpNotice">
                As etapas concluídas e os documentos anexados continuam registrados.
                {process.kind === "DESLIGAMENTO" ? " Nenhuma rescisão é gravada." : ""}
              </p>
              <div className="dpFormGroup">
                <label htmlFor="cancelReason">Motivo *</label>
                <textarea
                  id="cancelReason"
                  className="dpTextarea"
                  maxLength={500}
                  value={cancelReason}
                  disabled={busy}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>
            </div>
            <footer className="dpModal__footer">
              <button className="dpBtn dpBtn--secondary" onClick={() => setCancelOpen(false)} disabled={busy}>
                Voltar
              </button>
              <button className="dpBtn dpBtn--danger" onClick={cancelProcess} disabled={busy}>
                Cancelar processo
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {provisionalPassword && process ? (
        <ProvisionalPasswordModal
          password={provisionalPassword}
          cpf={process.employee.cpf}
          onClose={() => setProvisionalPassword(null)}
        />
      ) : null}
    </div>
  );
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="dpDefinition__label">{label}</div>
    <div className="dpDefinition__value">{value}</div>
  </div>
);
